import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import { Menu } from "primereact/menu";

const apiUrl = process.env.REACT_APP_API_URL;

// Petit toggle switch cohérent avec le reste des inputs compacts du formulaire
// (remplace les <input type="checkbox"> natifs, qui s'affichent en taille
// non maîtrisable selon les navigateurs)
const Toggle = ({ checked, onChange }) => (
  <div
    onClick={() => onChange(!checked)}
    role="checkbox"
    aria-checked={checked}
    style={{
      width: "34px",
      height: "19px",
      borderRadius: "10px",
      background: checked ? "#2563eb" : "#d1d5db",
      position: "relative",
      cursor: "pointer",
      flexShrink: 0,
      transition: "background 0.15s ease",
    }}
  >
    <div
      style={{
        width: "15px",
        height: "15px",
        borderRadius: "50%",
        background: "white",
        position: "absolute",
        top: "2px",
        left: checked ? "17px" : "2px",
        transition: "left 0.15s ease",
        boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
      }}
    />
  </div>
);

export default function DevisDemenagementDialog({
  visible,
  onHide,
  ficheData,
  onDevisGenerated,
  forceNew,
}) {
  // États pour la logistique
  const [nbDemenageurs, setNbDemenageurs] = useState(2);
  const [nbCamions, setNbCamions] = useState(1);
  const [nbVoyages, setNbVoyages] = useState(1);
  const [prixLogistique, setPrixLogistique] = useState(0);

  // Cartons
  const [carton, setCarton] = useState(false);
  const [nbCartons, setNbCartons] = useState(0);
  const [prixCarton, setPrixCarton] = useState(3);

  // Mise en carton
  const [miseEnCarton, setMiseEnCarton] = useState(false);
  const [prixMiseEnCarton, setPrixMiseEnCarton] = useState(0);

  // Démontage / remontage
  const [demontage, setDemontage] = useState(false);
  const [meublesDemontage, setMeublesDemontage] = useState("");
  const [prixDemontage, setPrixDemontage] = useState(0);

  const [remise, setRemise] = useState(0);
  const [options, setOptions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [devisInfo, setDevisInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const menuRef = useRef(null);

  const [isForceNew, setIsForceNew] = useState(forceNew);

  useEffect(() => {
    setIsForceNew(forceNew);
  }, [forceNew]);

  // États pour l'édition des infos client
  const [hasChanges, setHasChanges] = useState(false);
  const [tempNom, setTempNom] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [tempTel, setTempTel] = useState("");
  const [tempRegion, setTempRegion] = useState("");
  const [tempAdresse, setTempAdresse] = useState("");
  const [tempNote, setTempNote] = useState("");

  // Initialiser les données du client
  useEffect(() => {
    if (ficheData) {
      setTempNom(ficheData.nom || "");
      setTempEmail(ficheData.email || "");
      setTempTel(ficheData.tel || "");
      setTempRegion(ficheData.region || "");
      setTempAdresse(ficheData.adresse || "");
      setTempNote(ficheData.note || "");
      setHasChanges(false);
    }
  }, [ficheData]);

  // Vérifier les changements
  useEffect(() => {
    if (ficheData) {
      const hasAnyChange =
        tempNom !== (ficheData.nom || "") ||
        tempEmail !== (ficheData.email || "") ||
        tempTel !== (ficheData.tel || "") ||
        tempRegion !== (ficheData.region || "") ||
        tempAdresse !== (ficheData.adresse || "");

      setHasChanges(hasAnyChange);
    }
  }, [tempNom, tempEmail, tempTel, tempRegion, tempAdresse, ficheData]);

  useEffect(() => {
    if (visible && ficheData?.idFicheDem) {
      if (isForceNew) {
        setDevisInfo(null);
        setLoading(false);
        setNbDemenageurs(2);
        setNbCamions(1);
        setNbVoyages(1);
        setPrixLogistique(0);
        setCarton(false);
        setNbCartons(0);
        setPrixCarton(3);
        setMiseEnCarton(false);
        setPrixMiseEnCarton(0);
        setDemontage(false);
        setMeublesDemontage("");
        setPrixDemontage(0);
        setOptions([]);
        setRemise(0);
      } else if (!devisInfo) {
        checkExistingDevis();
      }
    }
  }, [visible, ficheData, isForceNew]);

  const checkExistingDevis = async () => {
    if (devisInfo?.a_devis === 1) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${apiUrl}/IHM/api/devis_demenagement.php?fiche_id=${ficheData.idFicheDem}`,
      );
      if (response.data.a_devis == 1) {
        setDevisInfo({
          a_devis: response.data.a_devis,
          numero_devis:
            response.data.dernier_devis_id || response.data.numero_devis,
          lien_devis: response.data.lien_devis,
          lien_pdf: response.data.lien_pdf,
          token_devis: response.data.token_devis,
          montant_devis: response.data.montant_total || 0,
          date_devis:
            response.data.date_devis ||
            response.data.date_creation ||
            new Date().toISOString(),
          idVosFactures: response.data.idVosFactures,
          statut_devis: response.data.statut_devis || "brouillon",
          nb_demenageurs: response.data.nb_demenageurs,
          nb_camions: response.data.nb_camions,
          nb_voyages: response.data.nb_voyages,
          carton: response.data.carton == 1,
          nb_cartons: response.data.nb_cartons,
          mise_en_carton: response.data.mise_en_carton == 1,
          demontage: response.data.demontage == 1,
          meubles_demontage: response.data.meubles_demontage,
        });
      } else {
        setDevisInfo(null);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du devis:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  // Calculs du devis
  const totalCartons = carton ? nbCartons * prixCarton : 0;
  const totalMiseEnCarton = miseEnCarton ? prixMiseEnCarton : 0;
  const totalDemontage = demontage ? prixDemontage : 0;

  const sousTotalPrestation =
    prixLogistique + totalCartons + totalMiseEnCarton + totalDemontage;

  const totalOptions = options.reduce(
    (sum, opt) => sum + (parseFloat(opt.prix) || 0),
    0,
  );

  const sousTotal = sousTotalPrestation + totalOptions;
  const montantRemise = (sousTotal * remise) / 100;
  const total = sousTotal - montantRemise;

  // Options libres
  const ajouterOption = () => {
    setOptions([...options, { titre: "", description: "", prix: 0 }]);
  };

  const supprimerOption = (index) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };

  const modifierOption = (index, champ, valeur) => {
    const newOptions = [...options];
    newOptions[index][champ] = valeur;
    setOptions(newOptions);
  };

  // Générer le devis
  const handleGenererDevis = async () => {
    if (!ficheData) return;
    setGenerating(true);
    try {
      const response = await axios.post(
        `${apiUrl}/IHM/api/devis_demenagement.php`,
        {
          fiche_id: ficheData.idFicheDem,
          nom: ficheData.nom,
          email: ficheData.email,
          telephone: ficheData.tel,
          region: ficheData.region,
          adresse: ficheData.adresse,
          nb_demenageurs: nbDemenageurs,
          nb_camions: nbCamions,
          nb_voyages: nbVoyages,
          prix_logistique: prixLogistique,
          carton: carton,
          nb_cartons: nbCartons,
          prix_carton: prixCarton,
          mise_en_carton: miseEnCarton,
          prix_mise_en_carton: prixMiseEnCarton,
          demontage: demontage,
          meubles_demontage: meublesDemontage,
          prix_demontage: prixDemontage,
          remise: remise,
          options: options,
          is_new_version: isForceNew,
          adminMod: localStorage.getItem("us"),
        },
      );

      if (response.data.success) {
        const newDevisInfo = {
          a_devis: 1,
          lien_devis: response.data.devis_url,
          lien_pdf: response.data.pdf_url,
          numero_devis: response.data.devis_number,
          montant_devis: response.data.montant,
          idVosFactures: response.data.idVosFactures || null,
          statut_devis: "brouillon",
          date_devis: new Date().toISOString(),
        };

        setDevisInfo(newDevisInfo);
        setIsForceNew(false);

        setSnackbarMessage("Devis généré avec succès !");
        setOpenSnackbar(true);
        if (onDevisGenerated) onDevisGenerated();
      } else {
        setSnackbarMessage(
          response.data.message || "Erreur lors de la génération",
        );
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Erreur:", error);
      setSnackbarMessage("Erreur lors de la génération du devis");
      setOpenSnackbar(true);
    } finally {
      setGenerating(false);
    }
  };

  // Sauvegarder les modifications client
  const handleSaveClient = async () => {
    try {
      await axios.post(`${apiUrl}/IHM/api/update_ficheDem.php`, {
        idFicheDem: ficheData.idFicheDem,
        nom: tempNom,
        telephone: tempTel,
        email: tempEmail,
        region: tempRegion,
        adresse: tempAdresse,
        note: tempNote,
        adminMod: localStorage.getItem("us"),
      });

      setHasChanges(false);
      setSnackbarMessage("Informations client mises à jour");
      setOpenSnackbar(true);

      if (onDevisGenerated) onDevisGenerated();
    } catch (error) {
      console.error("Erreur de mise à jour :", error);
      setSnackbarMessage("Erreur lors de la sauvegarde");
      setOpenSnackbar(true);
    }
  };

  // Header du dialog
  const header = (
    <div className="flex justify-content-between align-items-center w-full">
      <span className="font-semibold text-lg">
        Fiche client & devis déménagement
      </span>
    </div>
  );

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleCloseSnackbar}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      <Dialog
        blockScroll
        visible={visible}
        header={header}
        className="custom-dialog"
        style={{ width: "45vw", maxWidth: "700px" }}
        modal
        onHide={() => {
          onHide();
          setOptions([]);
          setIsForceNew(false);
          setDevisInfo(null);
          setLoading(false);
          setNbDemenageurs(2);
          setNbCamions(1);
          setNbVoyages(1);
          setPrixLogistique(0);
          setCarton(false);
          setNbCartons(0);
          setPrixCarton(3);
          setMiseEnCarton(false);
          setPrixMiseEnCarton(0);
          setDemontage(false);
          setMeublesDemontage("");
          setPrixDemontage(0);
          setRemise(0);
        }}
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
      >
        <div className="p-3">
          {/* Section 1 - Informations client */}
          <div className="grid">
            <div className="col-12">
              <div
                style={{
                  background: "#f3f4f6",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span
                    style={{
                      background: "#374151",
                      color: "white",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: 700,
                    }}
                  >
                    1
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#374151",
                      letterSpacing: "0.05em",
                    }}
                  >
                    INFORMATIONS CLIENT
                  </span>
                </div>

                {hasChanges && (
                  <span
                    onClick={() => handleSaveClient()}
                    style={{
                      border: "1px solid #22c55e",
                      borderRadius: "50%",
                      color: "#fff",
                      width: "28px",
                      height: "28px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      backgroundColor: "#22c55e",
                    }}
                  >
                    <SaveIcon sx={{ fontSize: 16 }} />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Infos client */}
          <div className="grid">
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Nom
              </label>
              <input
                value={tempNom}
                onChange={(e) => setTempNom(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Email
              </label>
              <input
                value={tempEmail}
                onChange={(e) => setTempEmail(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Téléphone
              </label>
              <input
                value={tempTel}
                onChange={(e) => setTempTel(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12 md:col-6">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Région
              </label>
              <input
                value={tempRegion}
                onChange={(e) => setTempRegion(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-6">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Adresse
              </label>
              <input
                value={tempAdresse}
                onChange={(e) => setTempAdresse(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Note
              </label>
              <textarea
                value={tempNote || ""}
                onChange={(e) => setTempNote(e.target.value)}
                rows={2}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                  resize: "vertical",
                }}
              />
            </div>
          </div>

          {/* Section 2 - Génération du devis */}
          {loading ? (
            <div className="flex justify-content-center p-5">
              <i
                className="pi pi-spin pi-spinner"
                style={{ fontSize: "2rem", color: "#2563eb" }}
              ></i>
            </div>
          ) : !devisInfo?.a_devis || isForceNew ? (
            <>
              <div className="grid">
                <div className="col-12">
                  <div
                    style={{
                      background: "#f3f4f6",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      marginBottom: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        background: "#374151",
                        color: "white",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: 700,
                      }}
                    >
                      2
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#374151",
                        letterSpacing: "0.05em",
                      }}
                    >
                      LOGISTIQUE
                    </span>
                  </div>
                </div>
              </div>

              {/* Logistique */}
              <div className="grid">
                <div className="col-12">
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Déménageurs
                      </label>
                      <input
                        type="number"
                        value={nbDemenageurs}
                        onChange={(e) =>
                          setNbDemenageurs(parseInt(e.target.value) || 0)
                        }
                        min={0}
                        style={{
                          width: "80px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Camions
                      </label>
                      <input
                        type="number"
                        value={nbCamions}
                        onChange={(e) =>
                          setNbCamions(parseInt(e.target.value) || 0)
                        }
                        min={0}
                        style={{
                          width: "80px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Voyages
                      </label>
                      <input
                        type="number"
                        value={nbVoyages}
                        onChange={(e) =>
                          setNbVoyages(parseInt(e.target.value) || 1)
                        }
                        min={1}
                        style={{
                          width: "80px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Prix logistique (€)
                      </label>
                      <input
                        type="number"
                        value={prixLogistique}
                        onChange={(e) =>
                          setPrixLogistique(parseFloat(e.target.value) || 0)
                        }
                        min={0}
                        step={1}
                        style={{
                          width: "110px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Remise %
                      </label>
                      <input
                        type="number"
                        value={remise}
                        onChange={(e) =>
                          setRemise(parseFloat(e.target.value) || 0)
                        }
                        min={0}
                        max={100}
                        style={{
                          width: "70px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cartons / Mise en carton / Démontage */}
              <div className="grid mt-2">
                <div className="col-12">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {/* Cartons */}
                    <div
                      style={{
                        display: "flex",
                        gap: "14px",
                        alignItems: "center",
                        flexWrap: "wrap",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        padding: "8px 10px",
                        minHeight: "48px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minWidth: "150px",
                        }}
                      >
                        <Toggle checked={carton} onChange={setCarton} />
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#374151",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Cartons
                        </span>
                      </div>

                      {carton && (
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "flex-end",
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ flex: "0 0 auto" }}>
                            <label
                              style={{
                                fontSize: "11px",
                                color: "#374151",
                                display: "block",
                                marginBottom: "2px",
                              }}
                            >
                              Nb cartons
                            </label>
                            <input
                              type="number"
                              value={nbCartons}
                              onChange={(e) =>
                                setNbCartons(parseInt(e.target.value) || 0)
                              }
                              min={0}
                              style={{
                                width: "80px",
                                height: "32px",
                                padding: "0 6px",
                                border: "1px solid #d1d5db",
                                borderRadius: "5px",
                                fontSize: "14px",
                              }}
                            />
                          </div>
                          <div style={{ flex: "0 0 auto" }}>
                            <label
                              style={{
                                fontSize: "11px",
                                color: "#374151",
                                display: "block",
                                marginBottom: "2px",
                              }}
                            >
                              Prix/carton (€)
                            </label>
                            <input
                              type="number"
                              value={prixCarton}
                              onChange={(e) =>
                                setPrixCarton(parseFloat(e.target.value) || 0)
                              }
                              min={0}
                              step={0.5}
                              style={{
                                width: "90px",
                                height: "32px",
                                padding: "0 6px",
                                border: "1px solid #d1d5db",
                                borderRadius: "5px",
                                fontSize: "14px",
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: "13px",
                              color: "#6b7280",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                            }}
                          >
                            = {totalCartons.toFixed(2)} €
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Mise en carton */}
                    <div
                      style={{
                        display: "flex",
                        gap: "14px",
                        alignItems: "center",
                        flexWrap: "wrap",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        padding: "8px 10px",
                        minHeight: "48px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minWidth: "150px",
                        }}
                      >
                        <Toggle
                          checked={miseEnCarton}
                          onChange={setMiseEnCarton}
                        />
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#374151",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Mise en carton
                        </span>
                      </div>

                      {miseEnCarton && (
                        <div style={{ flex: "0 0 auto" }}>
                          <label
                            style={{
                              fontSize: "11px",
                              color: "#374151",
                              display: "block",
                              marginBottom: "2px",
                            }}
                          >
                            Prix (€)
                          </label>
                          <input
                            type="number"
                            value={prixMiseEnCarton}
                            onChange={(e) =>
                              setPrixMiseEnCarton(
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            min={0}
                            step={1}
                            style={{
                              width: "100px",
                              height: "32px",
                              padding: "0 6px",
                              border: "1px solid #d1d5db",
                              borderRadius: "5px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Démontage / remontage */}
                    <div
                      style={{
                        display: "flex",
                        gap: "14px",
                        alignItems: "center",
                        flexWrap: "wrap",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        padding: "8px 10px",
                        minHeight: "48px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minWidth: "150px",
                        }}
                      >
                        <Toggle checked={demontage} onChange={setDemontage} />
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#374151",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Démontage / remontage
                        </span>
                      </div>

                      {demontage && (
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "flex-end",
                            flexWrap: "wrap",
                            flex: 1,
                          }}
                        >
                          <div style={{ flex: "1 1 200px", minWidth: "180px" }}>
                            <label
                              style={{
                                fontSize: "11px",
                                color: "#374151",
                                display: "block",
                                marginBottom: "2px",
                              }}
                            >
                              Meubles concernés
                            </label>
                            <input
                              value={meublesDemontage}
                              onChange={(e) =>
                                setMeublesDemontage(e.target.value)
                              }
                              placeholder="Lit, armoire, cuisine..."
                              style={{
                                width: "100%",
                                height: "32px",
                                padding: "0 8px",
                                border: "1px solid #d1d5db",
                                borderRadius: "5px",
                                fontSize: "14px",
                              }}
                            />
                          </div>
                          <div style={{ flex: "0 0 auto" }}>
                            <label
                              style={{
                                fontSize: "11px",
                                color: "#374151",
                                display: "block",
                                marginBottom: "2px",
                              }}
                            >
                              Prix (€)
                            </label>
                            <input
                              type="number"
                              value={prixDemontage}
                              onChange={(e) =>
                                setPrixDemontage(
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              min={0}
                              step={1}
                              style={{
                                width: "100px",
                                height: "32px",
                                padding: "0 6px",
                                border: "1px solid #d1d5db",
                                borderRadius: "5px",
                                fontSize: "14px",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Options libres */}
              <div className="grid mt-2">
                <div className="col-12 flex justify-content-between align-items-center">
                  <label
                    style={{
                      fontSize: "11px",
                      color: "#374151",
                      fontWeight: 600,
                    }}
                  >
                    Options
                  </label>
                  <button
                    onClick={ajouterOption}
                    style={{
                      width: "35px",
                      height: "35px",
                      border: "1px solid #d1d5db",
                      borderRadius: "5px",
                      background: "white",
                      fontSize: "20px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="grid">
                <div className="col-12">
                  {options.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-end",
                        marginTop: "8px",
                      }}
                    >
                      <div style={{ flex: 2 }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Titre
                        </label>
                        <input
                          value={option.titre}
                          onChange={(e) =>
                            modifierOption(index, "titre", e.target.value)
                          }
                          placeholder="Garde-meuble, stationnement..."
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div style={{ flex: 2 }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Description
                        </label>
                        <input
                          value={option.description}
                          onChange={(e) =>
                            modifierOption(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="Description"
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div style={{ width: "80px" }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Prix €
                        </label>
                        <input
                          type="number"
                          value={option.prix}
                          onChange={(e) =>
                            modifierOption(
                              index,
                              "prix",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0"
                          min={0}
                          step={1}
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div>
                        <button
                          onClick={() => supprimerOption(index)}
                          style={{
                            height: "35px",
                            width: "35px",
                            border: "1px solid #fca5a5",
                            borderRadius: "5px",
                            background: "white",
                            color: "#ef4444",
                            cursor: "pointer",
                            fontSize: "16px",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Récapitulatif du devis */}
              <div className="grid mt-2">
                <div className="col-12">
                  <div
                    style={{
                      background: "#f5f3eb",
                      borderRadius: "5px",
                      padding: "8px 12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "4px 0",
                        fontSize: "14px",
                      }}
                    >
                      <span>
                        Logistique ({nbDemenageurs} déménageur(s),{" "}
                        {nbCamions} camion(s), {nbVoyages} voyage(s))
                      </span>
                      <span>{prixLogistique.toFixed(2)} €</span>
                    </div>

                    {carton && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                        }}
                      >
                        <span>Cartons ({nbCartons})</span>
                        <span>{totalCartons.toFixed(2)} €</span>
                      </div>
                    )}

                    {miseEnCarton && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                        }}
                      >
                        <span>Mise en carton</span>
                        <span>{totalMiseEnCarton.toFixed(2)} €</span>
                      </div>
                    )}

                    {demontage && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                        }}
                      >
                        <span>
                          Démontage/remontage
                          {meublesDemontage ? ` (${meublesDemontage})` : ""}
                        </span>
                        <span>{totalDemontage.toFixed(2)} €</span>
                      </div>
                    )}

                    {options.map((opt, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                        }}
                      >
                        <span>{opt.titre || "Option"}</span>
                        <span>{(opt.prix || 0).toFixed(2)} €</span>
                      </div>
                    ))}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "6px 0",
                        borderTop: "1px solid #d1d5db",
                        marginTop: "4px",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      <span>Sous-total</span>
                      <span>{sousTotal.toFixed(2)} €</span>
                    </div>

                    {remise > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                          color: "#ef4444",
                        }}
                      >
                        <span>Remise ({remise}%)</span>
                        <span>- {montantRemise.toFixed(2)} €</span>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "6px 0",
                        borderTop: "1px solid #d1d5db",
                        marginTop: "4px",
                        fontWeight: 700,
                        fontSize: "15px",
                      }}
                    >
                      <span>Total TTC</span>
                      <span>{total.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bouton générer */}
              <div className="grid mt-2">
                <div className="col-12">
                  <Button
                    label={generating ? "Génération..." : "Générer le devis"}
                    icon={generating ? "pi pi-spin pi-spinner" : "pi pi-file"}
                    className="w-full"
                    style={{
                      background: "#dbeafe",
                      border: "1px solid #bfdbfe",
                      padding: "0.75rem",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: 500,
                      justifyContent: "center",
                      gap: "8px",
                      color: "#1d4ed8",
                    }}
                    onClick={handleGenererDevis}
                    disabled={generating || total <= 0}
                  />
                </div>
              </div>
            </>
          ) : (
            // Devis existant
            <>
              <div className="grid">
                <div className="col-12">
                  <div
                    className="p-3 border-round-sm"
                    style={{ background: "#f9fafb", borderRadius: "5px" }}
                  >
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Devis N° :</span>
                      <span>{devisInfo.numero_devis}</span>
                    </div>
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Date de création :</span>
                      <span>
                        {new Date(devisInfo.date_devis).toLocaleDateString(
                          "fr-FR",
                        )}
                      </span>
                    </div>
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Logistique :</span>
                      <span>
                        {devisInfo.nb_demenageurs} déménageur(s),{" "}
                        {devisInfo.nb_camions} camion(s),{" "}
                        {devisInfo.nb_voyages} voyage(s)
                      </span>
                    </div>
                    {devisInfo.carton == 1 && (
                      <div className="flex justify-content-between align-items-center mb-2">
                        <span className="font-bold">Cartons :</span>
                        <span>{devisInfo.nb_cartons} carton(s)</span>
                      </div>
                    )}
                    {devisInfo.demontage == 1 && (
                      <div className="flex justify-content-between align-items-center mb-2">
                        <span className="font-bold">Démontage :</span>
                        <span>{devisInfo.meubles_demontage}</span>
                      </div>
                    )}
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Montant total :</span>
                      <span>
                        {parseFloat(devisInfo.montant_devis).toFixed(2)} €
                      </span>
                    </div>
                    <div className="flex justify-content-between align-items-center">
                      <span className="font-bold">Statut :</span>
                      <span>
                        <span
                          className="p-tag"
                          style={{
                            background:
                              devisInfo.statut_devis === "envoye"
                                ? "#dcfce7"
                                : "#fef3c7",
                            color:
                              devisInfo.statut_devis === "envoye"
                                ? "#16a34a"
                                : "#d97706",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                        >
                          {devisInfo.statut_devis === "envoye"
                            ? "Envoyé"
                            : "Brouillon"}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions sur le devis */}
              <div className="grid mt-3">
                <div className="col-12">
                  <div className="flex gap-2 justify-content-center flex-wrap">
                    {devisInfo.lien_pdf && (
                      <Button
                        label="PDF"
                        severity="danger"
                        icon="pi pi-download"
                        style={{
                          background: "#fecaca",
                          border: "1px solid #fca5a5",
                          padding: "0.75rem",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 500,
                          justifyContent: "center",
                          gap: "8px",
                          color: "#dc2626",
                        }}
                        onClick={() =>
                          window.open(devisInfo.lien_pdf, "_blank")
                        }
                      />
                    )}
                    {devisInfo.lien_devis && (
                      <Button
                        label="Voir"
                        icon="pi pi-eye"
                        severity="info"
                        style={{
                          background: "#dbeafe",
                          border: "1px solid #bfdbfe",
                          padding: "0.75rem",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 500,
                          justifyContent: "center",
                          gap: "8px",
                          color: "#1d4ed8",
                        }}
                        onClick={() =>
                          window.open(devisInfo.lien_devis, "_blank")
                        }
                      />
                    )}
                    <Button
                      label="Modifier"
                      severity="warning"
                      icon="pi pi-pencil"
                      style={{
                        background: "#fed7aa",
                        border: "1px solid #fdba74",
                        padding: "0.75rem",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: 500,
                        justifyContent: "center",
                        gap: "8px",
                        color: "#c2410c",
                      }}
                      onClick={() =>
                        window.open(
                          `https://otedalons.vosfactures.fr/invoices/${devisInfo.idVosFactures}/edit`,
                          "_blank",
                        )
                      }
                    />
                    <Button
                      label="Envoyer le devis"
                      icon="pi pi-envelope"
                      severity="success"
                      style={{
                        background: "#dcfce7",
                        border: "1px solid #bbf7d0",
                        padding: "0.75rem",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: 500,
                        justifyContent: "center",
                        gap: "8px",
                        color: "#16a34a",
                      }}
                      onClick={() => {
                        axios
                          .post(`${apiUrl}/IHM/api/sendMailDevisDem.php`, {
                            id: ficheData.idFicheDem,
                            adminMod: localStorage.getItem("us"),
                          })
                          .then((res) => {
                            if (res.data.success) {
                              setSnackbarMessage(
                                `Le devis a bien été envoyé à ${ficheData.email}`,
                              );
                              setOpenSnackbar(true);
                              setDevisInfo({
                                ...devisInfo,
                                statut_devis: "envoye",
                              });

                              setTimeout(() => {
                                onHide();
                                setOptions([]);
                              }, 1500);
                            } else {
                              setSnackbarMessage(
                                res.data.message || "Envoi impossible",
                              );
                              setOpenSnackbar(true);
                            }
                          })
                          .catch((err) => {
                            console.error("Erreur envoi mail:", err);
                            setSnackbarMessage(
                              "Impossible d'envoyer le mail",
                            );
                            setOpenSnackbar(true);
                          });
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
}